import { useMemo, useCallback } from "react";
import { Plan } from "../features/common/types";

/* -------------------- types -------------------- */

/* -------------------- return types -------------------- */
export type PricingOption = { priceId: number; interval: string };

export type PriceResult = {
    planId: number;
    planName: string;
    priceId: number;
    interval: string;
    unitAmount: number;
    totalAmount: number;
};

export type UsePlanResult = {
    matchedPlan: Plan | null;
    planName: string | null;
    pricingOptions: PricingOption[]; // <-- priceId as value, interval as label
    getPriceById: (priceId: number) => PriceResult | null;
};

/* -------------------- hook -------------------- */
export function usePlanByLicenseCount(plans: Plan[] | undefined, licenseCount: number | undefined): UsePlanResult {
    console.log("usePlanByLicenseCount called with licenseCount:", licenseCount);
    console.log("Available plans:", plans);
    const matchedPlan = useMemo<Plan | null>(() => {
        if (!plans || typeof licenseCount !== "number" || isNaN(licenseCount)) return null;
        for (const p of plans) {
            if (p.status !== 1) continue;
            const min = p.min_users ?? 0;
            const max = p.max_users; // null => open-ended
            if (licenseCount >= min && (max === null || licenseCount <= max)) return p;
        }
        return null;
    }, [plans, licenseCount]);

    const planName = matchedPlan?.plan_name ?? null;

    const pricingOptions = useMemo<PricingOption[]>(() => {
        if (!matchedPlan) return [];
        const seen = new Set<number>();
        const opts: PricingOption[] = [];
        for (const pp of matchedPlan.PlanPrice ?? []) {
            if (!seen.has(pp.id)) {
                seen.add(pp.id);
                opts.push({ priceId: pp.id, interval: pp.interval });
            }
        }
        return opts;
    }, [matchedPlan]);

    const getPriceById = useCallback(
        (priceId: number): PriceResult | null => {
            if (!matchedPlan || typeof licenseCount !== "number" || isNaN(licenseCount)) return null;
            const priceEntry = (matchedPlan.PlanPrice || []).find((pp) => pp.id === priceId);
            if (!priceEntry) return null;
            const unitAmount = Number(priceEntry.unit_amount);
            const totalAmount = Number((unitAmount * licenseCount).toFixed(6));
            return {
                planId: matchedPlan.id,
                planName: matchedPlan.plan_name,
                priceId: priceEntry.id,
                interval: priceEntry.interval,
                unitAmount,
                totalAmount,
            };
        },
        [matchedPlan, licenseCount]
    );
    console.log("usePlanByLicenseCount results:", {
        matchedPlan,
        planName,
        intervalOptions: pricingOptions,
    });
    return {
        matchedPlan,
        planName,
        pricingOptions: pricingOptions,
        getPriceById,
    };
}
